﻿import { memo, useEffect, useMemo, useState } from "react";
import { FormControlLabel, Paper, Skeleton, Switch, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { DateTime } from "luxon";
import { HubConnectionState } from "@microsoft/signalr";
import { useQuery } from '@tanstack/react-query';
import { faIndustry, faListCheck, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { PlantStatusCardEntryDTO, PlantStatusMachineDTO } from "../../../DTOs/Dashboards/PlantStatusCardDataDTO";
import { plantStatusQuery } from "./dashboardQueries";
import { useSignalRContext } from "../../Contexts/SignalRContext";
import useShiftTimes from "../../Hooks/useShiftTimes";
import { ShiftLockState, isCurrentShift } from "../../../utilities/helpers/shiftLockStateHelpers";
import { LiveIndicatorState } from "../DashboardCard";
import { DashboardCardEmptyState } from "../DashboardCardEmptyState";
import { useOnlineStatus } from "../../Hooks/useOnlineStatus";
import { operationsMachineTarget, stoppagesMachineTarget } from "../../../utilities/helpers/dashboardLinks";
import { LinkTarget } from "../../../utilities/helpers/linkTarget";
import { useAccessibleTarget } from "../../AccessLink/useAccessibleTarget";
import {
  ShiftWindow,
  formatMachinePercent,
  machineStatusColour,
  machineStatusTiming,
  sortPlantStatusMachines,
  availabilityBand,
  resolveShiftScope,
} from "./plantStatusMachines";

import { shiftNameToLetter } from "../../../types/Shift";
type PlantStatusCardProps = {
  isFullScreen?: boolean
  shiftLockState: ShiftLockState | null;
  onLiveIndicatorChange?: (state: LiveIndicatorState) => void;
};

export const PlantStatusCard: React.FC<PlantStatusCardProps> = memo(({ isFullScreen, shiftLockState, onLiveIndicatorChange }) => {
  const { selectedDate, selectedShift, shiftStartTime, shiftEndTime } = useShiftTimes();
  const { connection } = useSignalRContext();
  const isOnline = useOnlineStatus();
  const queryEnabled = selectedDate.isValid && !!selectedShift;
  const [wantsFullDay, setWantsFullDay] = useState(false);
  const { fullDay, locked } = resolveShiftScope(
    selectedDate,
    selectedShift,
    wantsFullDay,
    shiftLockState?.currentShift ?? null
  );
  const { data: plantData = null, isLoading, dataUpdatedAt } = useQuery({
    ...plantStatusQuery(selectedDate, selectedShift, fullDay),
    enabled: queryEnabled,
  });

  const shift = useMemo<ShiftWindow>(
    () => ({ start: shiftStartTime, end: shiftEndTime }),
    [shiftStartTime, shiftEndTime]
  );

  const now = useMemo(
    () => (dataUpdatedAt > 0 ? DateTime.fromMillis(dataUpdatedAt) : DateTime.now()),
    [dataUpdatedAt]
  );

  const showSkeleton = isLoading || !queryEnabled;
  const isEmpty = !showSkeleton && !plantData?.entries?.length;

  useEffect(() => {
    const connected = connection?.state === HubConnectionState.Connected;
    const _isCurrentShift = isCurrentShift(selectedDate, shiftNameToLetter(selectedShift), shiftLockState);

    onLiveIndicatorChange?.({
      active: connected,
      showLabel: connected && _isCurrentShift,
      tooltipText: fullDay
        ? "Data displayed for every shift on this date"
        : _isCurrentShift
          ? "Data displayed for the latest shift"
          : "Data displayed for selected shift",
    });
  }, [connection?.state, fullDay, onLiveIndicatorChange, selectedDate, selectedShift, shiftLockState]);

  return (
    <Paper className="!shadow-none !border-none !w-full h-full text-center relative !rounded-none !bg-theme-surface-2 flex flex-col">
      <div className="flex justify-end p-3">
        <Tooltip
          title={locked
            ? "This date has no other shift yet, so there is nothing to take in."
            : "Take in every other shift recorded on this date alongside this one."}
          placement="left"
        >
          <span>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={fullDay}
                  disabled={locked}
                  onChange={(_, checked) => setWantsFullDay(checked)}
                />
              }
              label={<span className="text-xs md:text-sm text-theme-text">Whole day</span>}
            />
          </span>
        </Tooltip>
      </div>

      {showSkeleton && (
        <div className={`flex flex-wrap animate-fade-in justify-center px-3 ${isFullScreen ? "gap-x-2 gap-y-4 md:gap-x-8 md:gap-y-12" : "gap-x-1 gap-y-2 md:gap-x-4 md:gap-y-2"}`}> {/* Note: Loading placeholder*/}
          {[...Array(16)].map((_, i) => (
            <div
              key={i}
              className={`animate-pulse rounded-xl flex-1 min-w-[160px] max-w-[280px] shadow-md px-2 py-4 flex flex-col items-center justify-center bg-theme-table-heading ${isFullScreen ? "h-[250px] md:min-w-[300px] md:px-4 md:py-6" : "h-[170px]"}`}
            >
              <Skeleton animation="wave" variant="text" width="60%" height={28} className="bg-theme-table-row-even" />
              <Skeleton animation="wave" variant="text" width="40%" height={20} className="bg-theme-table-row-even" />
              <Skeleton animation="wave" variant="text" width="40%" height={20} className="bg-theme-table-row-even" />
              <Skeleton animation="wave" variant="text" width="50%" height={20} className="bg-theme-table-row-even" />
            </div>
          ))}
        </div>
      )}

      {isEmpty && (
        <DashboardCardEmptyState
          isFullScreen={isFullScreen}
          icon={faIndustry}
          title={isOnline ? "No plant status" : "Not available offline"}
          subtitle={isOnline
            ? "No plant availability has been recorded for the selected date and shift."
            : "Plant availability is calculated by the server, so it cannot be shown while offline. It is available again once you reconnect."}
        />
      )}
      {!isEmpty && (
        <div className={`flex-1 transition-all overflow-y-auto px-3 pt-1 ${!showSkeleton ? "opacity-100 fade-up" : "opacity-0"}`}>
          <div className="pb-2">
            <div className={`h-full flex flex-wrap justify-center ${isFullScreen ? "gap-x-2 gap-y-4 md:gap-x-8 md:gap-y-12" : "gap-x-1 gap-y-2 md:gap-x-4 md:gap-y-2"}`}>
              {plantData?.entries?.map((plant, i) => (
                <PlantStatusTile key={i} plant={plant} isFullScreen={isFullScreen} shift={shift} now={now} fullDay={fullDay} />
              ))}
            </div>
          </div>
        </div>
      )}
    </Paper>
  );
});

const AVAILABILITY_CLASSES = {
  success: { tile: "bg-theme-success", tooltip: "border-theme-success !shadow-theme-success" },
  warning: { tile: "bg-theme-warning", tooltip: "border-theme-warning !shadow-theme-warning" },
  danger: { tile: "bg-theme-danger", tooltip: "border-theme-danger !shadow-theme-danger" },
};

export const PlantStatusTile: React.FC<{
  plant: PlantStatusCardEntryDTO;
  isFullScreen?: boolean;
  shift: ShiftWindow;
  now: DateTime;
  fullDay: boolean;
}> = ({ plant, isFullScreen, shift, now, fullDay }) => {
  const machines = useMemo(() => sortPlantStatusMachines(plant.machines ?? []), [plant.machines]);
  const availability = AVAILABILITY_CLASSES[
    availabilityBand(plant.availability, plant.availabilityTargetPercentage)
  ];
  const tileClasses = `
    animate-fade-in rounded-xl flex-1 min-w-[160px] max-w-[280px] text-white px-2 py-4 flex flex-col items-center justify-center transition
    shadow-[0_0_4px_var(--theme-shadow)] hover:shadow-[0_0_8px_var(--theme-shadow)] border border-theme-table-heading hover:border-theme-table-heading-contrast
    hover:translate-y-[-2px]
    ${isFullScreen ? "h-[120px] md:h-[250px] md:min-w-[300px] md:px-4 md:py-6" : "h-[120px] md:h-[150px]"}
    ${availability.tile}
    `;

  const body = (
    <>
      <Typography variant="h6" className={`!font-bold text-white ${isFullScreen ? "md:!text-3xl md:!mb-2" : ""}`}>
        {plant.plantType}
      </Typography>
      <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
        Vehicles: {plant.vehicles}
      </Typography>
      <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
        UoA: {(plant.uofA * 100).toFixed(2)}%
      </Typography>
      <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
        Availability: {(plant.availability * 100).toFixed(2)}%
      </Typography>
      <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
        Asset Util': {(plant.assetUtilization * 100).toFixed(2)}%
      </Typography>
      <Typography variant="body2" className={`opacity-90 ${isFullScreen ? "md:!text-xl" : ""}`}>
        Erosion: {formatMachinePercent(plant.erosion)}
      </Typography>
    </>
  );

  if (machines.length === 0) return <div className={tileClasses}>{body}</div>;

  return (
    <Tooltip
      title={<MachineBreakdown plantType={plant.plantType} machines={machines} shift={shift} now={now} fullDay={fullDay} />}
      placement="bottom"
      enterTouchDelay={0}
      leaveTouchDelay={20_000}
      slotProps={{
        tooltip: {
          className: `!bg-theme-surface !text-theme-text !p-0 !m-0 !max-w-none border-2 !shadow !rounded-md overflow-hidden ${availability.tooltip}`,
        },
      }}
    >
      <div className={`${tileClasses}`}>{body}</div>
    </Tooltip>
  );
};

export const MachineBreakdown: React.FC<{
  plantType: string;
  machines: PlantStatusMachineDTO[];
  shift: ShiftWindow;
  now: DateTime;
  fullDay?: boolean;
}> = ({ plantType, machines, shift, now, fullDay }) => (
  <div className="w-[min(88vw,26rem)] max-h-[20rem] text-left flex flex-col">
    <div className="sticky top-0 flex items-baseline justify-between gap-2 px-4 py-3 bg-theme-surface-2 border-b border-theme-border">
      <span className="font-bold text-theme-text text-lg">{plantType}</span>
      <span className="text-theme-text-muted text-xs">
        {machines.length} {machines.length === 1 ? "machine" : "machines"}, {fullDay ? "whole day" : "this shift"}
      </span>
    </div>

    <div className="flex flex-col flex-1 overflow-y-auto">
      {machines.map(machine => (
        <MachineRow key={machine.plantId} machine={machine} shift={shift} now={now} />
      ))}
    </div>
  </div>
);

const MachineRow: React.FC<{ machine: PlantStatusMachineDTO; shift: ShiftWindow; now: DateTime }> = ({ machine, shift, now }) => {
  const timing = machineStatusTiming(machine, shift, now);

  return (
    <div className="flex items-center gap-2 px-5 py-3 border-b border-theme-border last:border-b-0">
      <span
        className="w-3.5 h-3.5 rounded-full shrink-0 border-2 border-theme-table-heading-border"
        style={{ backgroundColor: machineStatusColour(machine) }}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-bold text-theme-text text-sm">{machine.plantRef}</span>
          <span className="truncate text-theme-text-muted text-xs" title={machine.statusDetail ?? machine.status}>
            {machine.status}{timing ? ` ${timing}` : ""}
          </span>
        </div>
        <div className="text-theme-text-muted text-xs truncate">
          Avail {formatMachinePercent(machine.availabilityPercentage)}
          {" · "}
          UoA {formatMachinePercent(machine.utilizationOfAvailability)}
          {" · "}
          Erosion {formatMachinePercent(machine.erosion)}
          {machine.location ? ` · ${machine.location}` : ""}
        </div>
      </div>

      <div className="flex items-center gap-1 shrink-0">
        <MachineChip
          target={operationsMachineTarget(machine.plantRef, machine.plantId)}
          icon={faListCheck}
          label="Open in Operations"
        />
        <MachineChip
          target={stoppagesMachineTarget(machine.plantRef)}
          icon={faTriangleExclamation}
          label="Open in Stoppages"
        />
      </div>
    </div>
  );
};

const MachineChip: React.FC<{
  target: LinkTarget | null;
  icon: IconDefinition;
  label: string;
}> = ({ target, icon, label }) => {
  const accessible = useAccessibleTarget(target);

  if (!accessible) return null;

  return (
    <Link
      to={accessible.to}
      title={label}
      aria-label={label}
      className="flex items-center justify-center w-7 h-7 rounded-lg border border-theme-border bg-theme-surface-2 text-theme-text transition hover:border-theme-focus hover:bg-theme-surface"
    >
      <FontAwesomeIcon icon={icon} className="text-xs" />
    </Link>
  );
};
